import React, { useState, useMemo, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import { ReactComponent as ItinerarySunIcon } from '../../../icons/itinerary-sun.svg';
import { ReactComponent as CaretExpanded } from '../../../icons/caret-expanded.svg';
import { ReactComponent as AccommodationIcon } from '../../../icons/accommodation-itinerary.svg';
import { ReactComponent as TicketIcon } from '../../../icons/ticket-itinerary.svg';
import { ReactComponent as FlightIcon } from '../../../icons/flight-itinerary.svg';
import { ReactComponent as TransportIcon } from '../../../icons/transport-itinerary.svg';
import { ReactComponent as DefaultItineraryIcon } from '../../../icons/default-itinerary.svg';
import { ReactComponent as HourglassIcon } from '../../../icons/hourglass.svg';
import ArtifactOpener from './ArtifactOpener';
import './project-app-itinerary-block.scss';

type ItineraryIconType = 'accommodation' | 'ticket' | 'flight' | 'transport';

const itineraryIconMap: Record<
  string,
  React.FC<React.SVGProps<SVGSVGElement>>
> = {
  accommodation: AccommodationIcon,
  ticket: TicketIcon,
  flight: FlightIcon,
  transport: TransportIcon,
};

interface ItineraryCategory {
  id: string;
  label: string;
}

interface ItineraryItem {
  date: string;
  categoryId: string;
  timeSlot?: string | null;
  title: string;
  description?: string | null;
  status?: 'confirmed' | 'pending' | 'suggested' | 'booking_in_progress' | null;
  artifactId?: string | null;
  links?: Array<{ label: string; url: string }> | null;
  icon?: ItineraryIconType | null;
  groupId?: string | null;
  groupTitle?: string | null;
}

interface GroupedItem {
  items: ItineraryItem[];
  groupTitle: string;
  dateRange: string;
  icon?: ItineraryIconType | null;
  status?: 'confirmed' | 'pending' | 'suggested' | 'booking_in_progress' | null;
  description?: string | null;
  links?: Array<{ label: string; url: string }> | null;
  artifactId?: string | null;
}

interface ItineraryData {
  startDate: string;
  endDate: string;
  categories: ItineraryCategory[];
  items: ItineraryItem[];
  defaultView?: 'by_date' | 'by_category' | null;
}

interface ProjectAppItineraryBlockProps {
  blockId: string;
  data: Record<string, unknown>;
}

type ViewMode = 'by_date' | 'by_category';

const parseTimeSlotToMinutes = (slot: string): number | null => {
  if (!slot) return null;
  const lower = slot.trim().toLowerCase();

  // Named periods
  if (lower === 'morning') return 1; // 00:01
  if (lower === 'afternoon') return 721; // 12:01
  if (lower === 'evening') return 1021; // 17:01

  // 12-hour format: "1pm", "1:30pm", "07:30am", "12am"
  const match12 = lower.match(/^(\d{1,2})(?::(\d{2}))?\s*(am|pm)$/);
  if (match12) {
    let hours = parseInt(match12[1], 10);
    const mins = match12[2] ? parseInt(match12[2], 10) : 0;
    const period = match12[3];
    if (period === 'am' && hours === 12) hours = 0;
    else if (period === 'pm' && hours !== 12) hours += 12;
    return hours * 60 + mins;
  }

  // 24-hour format: "09:00", "17:30", "23:59"
  const match24 = lower.match(/^(\d{1,2}):(\d{2})$/);
  if (match24) {
    const hours = parseInt(match24[1], 10);
    const mins = parseInt(match24[2], 10);
    if (hours >= 0 && hours <= 23 && mins >= 0 && mins <= 59) {
      return hours * 60 + mins;
    }
  }

  return null;
};

const sortByTimeSlot = (a: ItineraryItem, b: ItineraryItem): number => {
  const minsA = parseTimeSlotToMinutes(a.timeSlot || '');
  const minsB = parseTimeSlotToMinutes(b.timeSlot || '');

  // Items without a parseable time go last
  if (minsA === null && minsB === null) return 0;
  if (minsA === null) return 1;
  if (minsB === null) return -1;

  return minsA - minsB;
};

const ProjectAppItineraryBlock: React.FC<ProjectAppItineraryBlockProps> = ({
  blockId,
  data,
}) => {
  const itinerary = data as unknown as ItineraryData;
  const categories = itinerary.categories || [];
  const items = itinerary.items || [];
  const [activeView, setActiveView] = useState<ViewMode>(
    itinerary.defaultView || 'by_category',
  );
  const [expandedSections, setExpandedSections] = useState<Set<string>>(() => {
    const keys = new Set<string>();
    for (const item of itinerary.items || []) {
      keys.add(item.date);
      keys.add(item.categoryId);
    }
    return keys;
  });

  const toggleSection = useCallback((key: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  const categoryMap = useMemo(() => {
    const map = new Map<string, ItineraryCategory>();
    for (const cat of categories) {
      map.set(cat.id, cat);
    }
    return map;
  }, [categories]);

  const formatDate = useCallback(
    (dateStr: string, includeYear = true): string => {
      try {
        const d = new Date(dateStr + 'T00:00:00');
        const options: Intl.DateTimeFormatOptions = {
          weekday: 'short',
          day: 'numeric',
          month: 'short',
          ...(includeYear && { year: 'numeric' }),
        };
        return d.toLocaleDateString('en-GB', options);
      } catch {
        return dateStr;
      }
    },
    [],
  );

  const byDateGroups = useMemo(() => {
    const dateMap = new Map<string, ItineraryItem[]>();
    for (const item of items) {
      const existing = dateMap.get(item.date) || [];
      existing.push(item);
      dateMap.set(item.date, existing);
    }
    return Array.from(dateMap.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(
        ([date, dateItems]) =>
          [date, dateItems.sort(sortByTimeSlot)] as [string, ItineraryItem[]],
      );
  }, [items]);

  const byCategoryGroups = useMemo(() => {
    const catMap = new Map<string, ItineraryItem[]>();
    for (const item of items) {
      const existing = catMap.get(item.categoryId) || [];
      existing.push(item);
      catMap.set(item.categoryId, existing);
    }
    return categories
      .filter((cat) => catMap.has(cat.id))
      .map((cat) => {
        const sorted = (catMap.get(cat.id) || []).sort(
          (a, b) => a.date.localeCompare(b.date) || sortByTimeSlot(a, b),
        );
        const groupMap = new Map<string, ItineraryItem[]>();
        for (const item of sorted) {
          if (item.groupId) {
            const existing = groupMap.get(item.groupId) || [];
            existing.push(item);
            groupMap.set(item.groupId, existing);
          }
        }
        const entries: (ItineraryItem | GroupedItem)[] = [];
        const seenGroups = new Set<string>();
        for (const item of sorted) {
          if (item.groupId) {
            if (seenGroups.has(item.groupId)) continue;
            seenGroups.add(item.groupId);
            const groupItems = groupMap.get(item.groupId)!;
            const dates = groupItems.map((i) => i.date).sort();
            const first = groupItems.find((i) => i.groupTitle) || groupItems[0];
            entries.push({
              items: groupItems,
              groupTitle: first.groupTitle || first.title,
              dateRange:
                dates.length > 1 && dates[0] !== dates[dates.length - 1]
                  ? `${formatDate(dates[0], false)} – ${formatDate(dates[dates.length - 1])}`
                  : formatDate(dates[0]),
              icon: first.icon,
              status: first.status,
              description: groupItems
                .map((i) => i.description)
                .filter(Boolean)
                .join('\n\n'),
              links: groupItems.flatMap((i) => i.links || []),
              artifactId:
                groupItems.find((i) => i.artifactId)?.artifactId || null,
            } as GroupedItem);
          } else {
            entries.push(item);
          }
        }
        return { category: cat, entries };
      });
  }, [items, categories, formatDate]);

  const renderItem = (item: ItineraryItem, index: number) => {
    const cat = categoryMap.get(item.categoryId);
    return (
      <div
        key={`${item.date}-${item.categoryId}-${index}`}
        className='itinerary-item'
      >
        <div className={`icon-area${item.status ? ` ${item.status}` : ''}`}>
          {(() => {
            if (item.status === 'booking_in_progress') {
              return (
                <HourglassIcon className='itinerary-item-icon hourglass' />
              );
            }
            const IconComp = item.icon
              ? itineraryIconMap[item.icon]
              : DefaultItineraryIcon;
            return IconComp ? (
              <IconComp
                className={`itinerary-item-icon ${item.icon || 'default'}`}
              />
            ) : null;
          })()}
        </div>
        <div className='item-main-content'>
          {activeView === 'by_category' && (
            <span className='itinerary-item-date'>{formatDate(item.date)}</span>
          )}
          <div className='itinerary-item-row'>
            <div className='slot-wrapper'>
              {item.timeSlot && (
                <span className='itinerary-item-time'>{item.timeSlot}: </span>
              )}
              <span className='itinerary-item-title'>{item.title}</span>{' '}
            </div>
            {item.artifactId && <ArtifactOpener artifactId={item.artifactId} />}
          </div>
          {item.description && (
            <div className='itinerary-item-description'>
              <ReactMarkdown>{item.description}</ReactMarkdown>
            </div>
          )}
          <div className='itinerary-item-meta'>
            {item.links && item.links.length > 0 && (
              <>
                {item.links.map((link, linkIdx) => (
                  <a
                    key={linkIdx}
                    href={link.url}
                    target='_blank'
                    rel='noopener noreferrer'
                    className='itinerary-item-link'
                  >
                    {link.label}
                  </a>
                ))}
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderGroupedItem = (group: GroupedItem, index: number) => {
    const IconComp = group.icon
      ? itineraryIconMap[group.icon]
      : DefaultItineraryIcon;
    return (
      <div
        key={`group-${group.groupTitle}-${index}`}
        className='itinerary-item'
      >
        <div className={`icon-area${group.status ? ` ${group.status}` : ''}`}>
          {group.status === 'booking_in_progress' ? (
            <HourglassIcon className='itinerary-item-icon hourglass' />
          ) : IconComp ? (
            <IconComp
              className={`itinerary-item-icon ${group.icon || 'default'}`}
            />
          ) : null}
        </div>
        <div className='item-main-content'>
          {' '}
          <span className='itinerary-item-date'>{group.dateRange}</span>
          <div className='itinerary-item-row'>
            <span className='itinerary-item-title'>
              <span>{group.groupTitle}</span>
            </span>
            {group.artifactId && (
              <ArtifactOpener artifactId={group.artifactId} />
            )}
          </div>
          <div className='itinerary-item-meta'>
            {group.links && group.links.length > 0 && (
              <>
                {group.links.map((link, linkIdx) => (
                  <a
                    key={linkIdx}
                    href={link.url}
                    target='_blank'
                    rel='noopener noreferrer'
                    className='itinerary-item-link'
                  >
                    {link.label}
                  </a>
                ))}
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className='itinerary-block' data-block-id={blockId}>
      <div className='itinerary-header'>
        <div className='itinerary-date-range'>
          <ItinerarySunIcon className='itinerary-sun-icon' />
          {formatDate(
            itinerary.startDate,
            itinerary.startDate.slice(0, 4) !== itinerary.endDate.slice(0, 4),
          )}{' '}
          — {formatDate(itinerary.endDate)}
        </div>
        <div className='itinerary-view-toggle'>
          <button
            className={`itinerary-toggle-btn${activeView === 'by_date' ? ' active' : ''}`}
            onClick={() => setActiveView('by_date')}
          >
            Date
          </button>
          <button
            className={`itinerary-toggle-btn${activeView === 'by_category' ? ' active' : ''}`}
            onClick={() => setActiveView('by_category')}
          >
            Category
          </button>
        </div>
      </div>

      <hr />

      <div className='itinerary-content'>
        {activeView === 'by_date' &&
          byDateGroups.map(([date, dateItems]) => {
            const isExpanded = expandedSections.has(date);
            return (
              <div key={date} className='itinerary-section'>
                <div
                  className='itinerary-section-header'
                  onClick={() => toggleSection(date)}
                >
                  <div
                    className={`itinerary-caret${isExpanded ? ' expanded' : ''}`}
                  >
                    <CaretExpanded />
                  </div>
                  {formatDate(date)}
                </div>
                <div
                  className={`itinerary-section-collapse${isExpanded ? ' expanded' : ''}`}
                >
                  <div className='itinerary-section-items'>
                    {dateItems.map((item: ItineraryItem, idx: number) =>
                      renderItem(item, idx),
                    )}
                  </div>
                </div>
              </div>
            );
          })}

        {activeView === 'by_category' &&
          byCategoryGroups.map(({ category, entries }) => {
            const isExpanded = expandedSections.has(category.id);
            return (
              <div key={category.id} className='itinerary-section'>
                <div
                  className='itinerary-section-header'
                  onClick={() => toggleSection(category.id)}
                >
                  <div
                    className={`itinerary-caret${isExpanded ? ' expanded' : ''}`}
                  >
                    <CaretExpanded />
                  </div>
                  {category.label}
                </div>
                <div
                  className={`itinerary-section-collapse${isExpanded ? ' expanded' : ''}`}
                >
                  <div className='itinerary-section-items'>
                    {entries.map((entry, idx) =>
                      'groupTitle' in entry
                        ? renderGroupedItem(entry as GroupedItem, idx)
                        : renderItem(entry as ItineraryItem, idx),
                    )}
                  </div>
                </div>
              </div>
            );
          })}

        {items.length === 0 && (
          <div className='itinerary-empty'>No items yet</div>
        )}
      </div>
    </div>
  );
};

export default ProjectAppItineraryBlock;
